import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface BannerSettings {
  enabled: boolean;
  text_sv: string | null;
  text_en: string | null;
  text_ar: string | null;
  text: string | null;
  animation_speed: number | null;
}

interface SiteData {
  banner: BannerSettings | null;
  headerLogoUrl: string | null;
}

/**
 * Aggregated hook for site-wide data (banner + logo).
 * Single query with aggressive caching to reduce initial API calls.
 */
export const useSiteData = () => {
  return useQuery({
    queryKey: ['site-data'],
    queryFn: async (): Promise<SiteData> => {
      // Parallel fetch for banner + site info
      const [bannerRes, siteInfoRes] = await Promise.all([
        supabase.from('banner_settings').select('enabled, text_sv, text_en, text_ar, text, animation_speed').single(),
        supabase.from('site_information').select('header_logo_url').single()
      ]);

      const banner = bannerRes.data?.enabled ? bannerRes.data : null;
      const headerLogoUrl = siteInfoRes.data?.header_logo_url || null;

      return { banner, headerLogoUrl };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - site data rarely changes
    gcTime: 30 * 60 * 1000,   // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    // Don't block initial render - header can render without banner initially
    refetchOnMount: 'always',
  });
};
